import React, { useState, useCallback } from 'react';
import { Tabs, Card, Space, Select, Input, Segmented, Button, Typography } from 'antd';
import { ReloadOutlined, SearchOutlined, FileTextOutlined, CloudServerOutlined } from '@ant-design/icons';
import { monitoringAPI } from '../services/api';
import { gray } from '../theme/tokens';

interface LogLine {
  timestamp: string;
  level: string;
  message: string;
}

const LOG_LEVEL_COLORS: Record<string, string> = {
  ERROR: '#ef4444', WARNING: '#f59e0b', INFO: '#3b82f6', DEBUG: '#94a3b8', SUCCESS: '#10b981',
};

/* ─── Terminal-style Log Viewer ─── */
const LogViewer: React.FC<{ lines: LogLine[]; loading: boolean; emptyText: string; footerLeft?: string; footerRight?: string }> = ({
  lines, loading, emptyText, footerLeft, footerRight,
}) => (
  <>
    <div
      style={{
        height: 500, overflow: 'auto', background: '#0f172a',
        borderRadius: 8, padding: '12px 16px',
        fontFamily: "'D2Coding', 'Menlo', 'Consolas', ui-monospace, monospace",
        fontSize: 11, lineHeight: 1.7,
      }}
    >
      {lines.length === 0 && !loading && (
        <div style={{ color: '#475569', textAlign: 'center', paddingTop: 60 }}>
          {emptyText}
        </div>
      )}
      {lines.map((line, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, borderBottom: '1px solid #1e293b', padding: '2px 0' }}>
          {line.timestamp && (
            <span style={{ color: '#475569', flexShrink: 0, minWidth: 190 }}>
              {line.timestamp.replace('T', ' ').substring(0, 23)}
            </span>
          )}
          <span style={{
            color: LOG_LEVEL_COLORS[line.level] || '#94a3b8',
            fontWeight: 700, minWidth: 52, flexShrink: 0,
          }}>
            {line.level}
          </span>
          <span style={{
            color: line.level === 'ERROR' ? '#fca5a5' : '#e2e8f0',
            wordBreak: 'break-all',
          }}>
            {line.message}
          </span>
        </div>
      ))}
    </div>
    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: gray[400] }}>
      <span>{footerLeft || `${lines.length}줄 표시`}</span>
      {footerRight && <span>{footerRight}</span>}
    </div>
  </>
);

/* ─── Tab 1: Container Logs ─── */
const ContainerLogTab: React.FC = () => {
  const [logContainer, setLogContainer] = useState('sappt-backend');
  const [logLines, setLogLines] = useState<LogLine[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logSearch, setLogSearch] = useState('');
  const [logLevel, setLogLevel] = useState('');
  const [logCount, setLogCount] = useState(100);

  const fetchLogs = useCallback(async () => {
    setLogLoading(true);
    try {
      const res = await monitoringAPI.containerLogs({
        container: logContainer, lines: logCount, search: logSearch, level: logLevel,
      });
      setLogLines(res.data.lines || []);
    } catch {
      setLogLines([]);
    } finally {
      setLogLoading(false);
    }
  }, [logContainer, logCount, logSearch, logLevel]);

  return (
    <Card size="small" style={{ borderRadius: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Space size={8} wrap>
          <Select
            value={logContainer}
            onChange={(v) => setLogContainer(v)}
            style={{ width: 180 }}
            size="small"
            options={[
              { label: 'Backend (k3s)', value: 'sappt-backend' },
              { label: 'Frontend (k3s)', value: 'sappt-frontend' },
              { label: 'Partner FE (k3s)', value: 'sappt-partner-frontend' },
              { label: 'PostgreSQL', value: 'sappt-postgres' },
              { label: 'TAPO API', value: 'sappt-tapo-api' },
              { label: 'Grafana', value: 'sappt-grafana' },
              { label: 'Nginx PM', value: 'nginx-proxy-manager' },
            ]}
          />
          <Segmented
            size="small"
            value={logLevel}
            onChange={(v) => setLogLevel(v as string)}
            options={[
              { label: 'ALL', value: '' },
              { label: 'ERROR', value: 'ERROR' },
              { label: 'WARN', value: 'WARNING' },
              { label: 'INFO', value: 'INFO' },
            ]}
          />
          <Input
            size="small"
            placeholder="Search logs..."
            prefix={<SearchOutlined />}
            style={{ width: 160 }}
            value={logSearch}
            onChange={(e) => setLogSearch(e.target.value)}
            onPressEnter={fetchLogs}
            allowClear
          />
          <Select
            value={logCount}
            onChange={setLogCount}
            size="small"
            style={{ width: 80 }}
            options={[
              { label: '50', value: 50 },
              { label: '100', value: 100 },
              { label: '200', value: 200 },
              { label: '500', value: 500 },
            ]}
          />
        </Space>
        <Button size="small" icon={<ReloadOutlined />} onClick={fetchLogs} loading={logLoading}>
          조회
        </Button>
      </div>
      <LogViewer
        lines={logLines}
        loading={logLoading}
        emptyText={'"조회" 버튼을 클릭하여 로그를 불러오세요'}
        footerLeft={`${logLines.length}줄 표시`}
        footerRight={`Container: ${logContainer}`}
      />
    </Card>
  );
};

/* ─── Tab 2: System Logs ─── */
const SystemLogTab: React.FC = () => {
  const [logFile, setLogFile] = useState('backup.log');
  const [logLines, setLogLines] = useState<LogLine[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logCount, setLogCount] = useState(100);
  const [errorMsg, setErrorMsg] = useState('');

  const fetchLogs = useCallback(async () => {
    setLogLoading(true);
    setErrorMsg('');
    try {
      const res = await monitoringAPI.systemLogs({ file: logFile, lines: logCount });
      setLogLines(res.data.lines || []);
    } catch {
      setLogLines([]);
      setErrorMsg('로그를 불러올 수 없습니다');
    } finally {
      setLogLoading(false);
    }
  }, [logFile, logCount]);

  return (
    <Card size="small" style={{ borderRadius: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Space size={8} wrap>
          <Select
            value={logFile}
            onChange={(v) => setLogFile(v)}
            style={{ width: 220 }}
            size="small"
            options={[
              { label: 'backup.log', value: 'backup.log' },
              { label: 'discord_report.log', value: 'discord_report.log' },
            ]}
          />
          <Select
            value={logCount}
            onChange={setLogCount}
            size="small"
            style={{ width: 80 }}
            options={[
              { label: '50', value: 50 },
              { label: '100', value: 100 },
              { label: '200', value: 200 },
              { label: '500', value: 500 },
            ]}
          />
        </Space>
        <Button size="small" icon={<ReloadOutlined />} onClick={fetchLogs} loading={logLoading}>
          조회
        </Button>
      </div>
      {errorMsg ? (
        <div style={{
          height: 500, background: '#0f172a', borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography.Text style={{ color: '#ef4444', fontSize: 14 }}>{errorMsg}</Typography.Text>
        </div>
      ) : (
        <LogViewer
          lines={logLines}
          loading={logLoading}
          emptyText={'"조회" 버튼을 클릭하여 로그를 불러오세요'}
          footerLeft={`${logLines.length}줄 표시`}
          footerRight={`File: ${logFile}`}
        />
      )}
    </Card>
  );
};

/* ─── Main Page ─── */
const ServerLogPage: React.FC = () => {
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        marginBottom: 20, padding: '16px 20px', background: '#fff', borderRadius: 12,
        border: '1px solid #e2e8f0',
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: 'linear-gradient(135deg, #4338ca, #6366f1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <FileTextOutlined style={{ color: '#fff', fontSize: 20 }} />
        </div>
        <div>
          <Typography.Title level={5} style={{ margin: 0, fontSize: 16 }}>
            서버 로그
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            컨테이너 및 시스템 로그 조회
          </Typography.Text>
        </div>
      </div>

      <Tabs
        defaultActiveKey="container"
        items={[
          {
            key: 'container',
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <CloudServerOutlined /> 컨테이너 로그
              </span>
            ),
            children: <ContainerLogTab />,
          },
          {
            key: 'system',
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FileTextOutlined /> 시스템 로그
              </span>
            ),
            children: <SystemLogTab />,
          },
        ]}
      />
    </div>
  );
};

export default ServerLogPage;
